import React, { useRef } from 'react';
import { IconButton } from '@cbrebuild/blocks';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import Percent from '../../../nucleus/formats/percent';
import VOUCHER_ROUTES from '../../../page-templates/voucher/voucher-subroutes';
import formatAsCurrency from '../../../utils/format-as-currency';
import { useRegisterRequiredRows, useRegisterRequiredField, useRegisterAddRowAction } from './RequiredFieldsContext';

const InstallmentsReview = () => {
  const {
    deal, voucher, voucherInstallments,
  } = useSelector(state => ({
    deal: state.deal.deal,
    voucher: state.voucher.voucher,
    voucherInstallments: state.voucherInstallments,
  }));
  const history = useHistory();
  const url = VOUCHER_ROUTES[2].routes[3].path.replace(':id', voucher.deal);
  const requiredText = <p className="voucher-error-text">Required</p>;

  // Write to voucher_valid so ReviewTab banner + submit button respond correctly.
  // No installments at all is invalid. Any installment missing date or (for lease) due-upon is invalid.
  const hasNoInstallments = !voucherInstallments.length;
  const hasInvalidInstallment = voucherInstallments.some(inst =>
    !inst.installment_date ||
    (deal.deal_type === 'lease' && !inst.voucher_contingency_code)
  );
  if ((hasNoInstallments || hasInvalidInstallment) && deal.deal_type === 'lease') {
    localStorage.setItem('voucher_valid', JSON.stringify(false));
  }

  // Shared by the row registration below and the "at least one installment" checks further
  // down, so both agree on what counts as a fully filled-out installment.
  const isInstallmentMissing = installment => !installment.installment_date
    || (deal.deal_type === 'lease' && !installment.voucher_contingency_code)
    || !(installment.installment_amount || installment.installment_amount === 0);

  useRegisterRequiredRows(
    voucherInstallments,
    {
      getFieldId: installment => `installment-${installment.id}`,
      isMissing: isInstallmentMissing,
      buildEntry: installment => ({
        section: 'Installments',
        label: installment.voucher_contingency_code || 'Installment',
        customRowType: 'installment',
        rowGroup: 'installment',
        rowId: installment.id,
      }),
    },
    [voucherInstallments, deal.deal_type],
  );

  // Registers the "You must add at least one installment" message into the "Before you
  // submit" panel until at least one installment row is FULLY filled out -- a row that
  // already exists (however it was created, e.g. from elsewhere in the voucher flow) but is
  // still missing required fields does not satisfy this, matching the row itself still
  // showing up as incomplete above. Matches the same lease-only gate the `voucher_valid`
  // check above already applies for a fully-empty installments list.
  const installmentListMissing = deal.deal_type === 'lease'
    && !voucherInstallments.some(installment => !isInstallmentMissing(installment));
  useRegisterRequiredField(
    'installment-list-required',
    {
      isReview: true,
      missing: installmentListMissing,
      buildEntry: () => ({
        section: 'Installments',
        label: 'Installment',
        requiredMessage: `You must add at least one installment to this ${voucher.is_early_invoice ? 'early invoice' : 'voucher'}`,
        // Once any installment row exists (even incomplete), ITS OWN missing-row entry
        // (registered above via useRegisterRequiredRows) already represents this same gap in
        // the "N required fields" footer count -- stop double-counting this message too
        // (it still keeps showing/staying red until a row is complete, just excluded here).
        excludeFromCount: voucherInstallments.length > 0,
      }),
    },
    [installmentListMissing, voucher.is_early_invoice, voucherInstallments.length],
  );

  // Registers the "Add Installment" button into the panel -- reusing the exact same
  // `addInstallment` add button the live Installments tab renders. Freezes whether the
  // requirement was outstanding at the moment this component first mounted, so a user
  // adding several installments in one sitting keeps seeing the button, but switching
  // tabs/screens (a fresh mount) re-evaluates it and hides the button (and the whole
  // Installments section, once it has no other registered entries left) if an installment
  // already exists by then -- positioned directly below the installment rows via
  // `rowGroup: 'installment'` + `isAddButton` (see `orderSectionEntries` in
  // `RequiredFieldsSummary.js`) while it is shown.
  const initialInstallmentListMissingRef = useRef(null);
  if (initialInstallmentListMissingRef.current === null) {
    initialInstallmentListMissingRef.current = installmentListMissing;
  }
  useRegisterAddRowAction(
    'installment-add-button',
    initialInstallmentListMissingRef.current,
    () => ({
      section: 'Installments', customRowType: 'installmentAdd', rowGroup: 'installment', isAddButton: true, rowProps: { dealId: deal.id },
    }),
    [deal.id, initialInstallmentListMissingRef.current],
  );

  return (
    <div className="voucher-review-group installments-review">
      <div className="navigation">
        <Link className="h2 link" to={url}>Installments</Link>
        <IconButton iconName="edit" onClick={() => { history.push(url); }} />
      </div>
      <div className="content">
        {!voucherInstallments.length &&
          <div className="voucher-error-text">You must add at least one installment to this {voucher.is_early_invoice ? 'early invoice' : 'voucher'}</div>}
        {voucherInstallments.length ?
          <table>
            <colgroup>
              <col name="due upon" />
              <col />
              <col name="allocation" />
              <col name="amount" />
              <col name="due date" />
            </colgroup>
            <tbody>
              <tr className="borderless-row">
                <th>{deal.deal_type === 'lease' && <>Due Upon</>}</th>
                <th />
                <th>Allocation</th>
                <th>Amount</th>
                <th>Due Date</th>
              </tr>
              {voucherInstallments.map(installment => (
                <tr key={installment.id}>
                  <td>{deal.deal_type === 'lease' && (installment.voucher_contingency_code || requiredText)}</td>
                  <td />
                  <td><Percent decimalRounding={2} value={Number(installment.voucher_installment_percent)} emptyState="0%" /></td>
                  <td>{(installment.installment_amount || installment?.installment_amount === 0)
                    ? formatAsCurrency(installment.installment_amount) : requiredText}
                  </td>
                  <td>{installment.installment_date ? moment(installment.installment_date).format('ll') : requiredText}</td>
                </tr>
              ))}
            </tbody>
          </table>
        : ''}
      </div>
    </div>
  );
};

export default InstallmentsReview;
